import { ChildRef } from '../../../../../lib/web/components/child-ref';
import { ChildrenRef } from '../../../../../lib/web/components/children-ref';
import { ClickListener } from '../../../../../lib/web/components/click-listener';
import { Component } from '../../../../../lib/web/components/component';
import { ComponentBase } from '../../../../../lib/web/components/component-base';
import { EventListener } from '../../../../../lib/web/components/event-listener';
import { Input } from '../../../../../lib/web/components/input';
import { DateTime } from '../../../../../lib/web/core/date-time';
import { Str } from '../../../../../lib/web/core/str';
import { CapsuleSegment } from '../../../../../shared/server/model/capsule-segment';
import { ApiService } from '../../../services/api.service';
import { DataEventService } from '../../../services/data-event.service';
import { MediaOptions, MediaPlayerService, MediaType } from '../../../services/media-player.service';
import { SessionService } from '../../../services/session.service';
import { UserWatchingHistoryService } from '../../../services/user-watching-history.service';
import { UserFavoriteService } from '../../../services/user-favorite.service';
import { IconComponent } from '../../atoms/icon/icon.component';
import { ItemSelectorDropdownComponent } from '../../atoms/item-selector-dropdown/item-selector-dropdown.component';
import { TabComponent } from '../../atoms/tab/tab.component';
import { GridComponent } from '../../organisms/grid/grid.component';

@Component({
    selector: '.p-capsule'
})
export class CapsuleComponent extends ComponentBase<HTMLElement> {

    private _playerBack: boolean = false;
    private _segmentIsOpened: boolean = false;

    @Input('id')
    private _capsuleId: number = null;
    @Input('type')
    private _type: number = null;
    @Input('collectionId')
    private _collectionId: number = null;
    @Input('isComingSoon')
    private _isComingSoon: boolean = null;
    @Input('playerType')
    private _playerType: 'brightcove' | 'v2' = null;
    @Input('image')
    private _image: string = null;
    @Input('segments', { isJSON: true })
    private _segments: CapsuleSegment[] = null;
    @Input('freePlay')
    private _freePlay: boolean = null;
    @Input('licenseStart')
    private _licenseStart: Date = null;
    @Input('licenseEnd')
    private _licenseEnd: Date = null;

    @ChildRef('.p-capsule__segments')
    private _selectSegmentElement: HTMLButtonElement = null;
    @ChildRef('.p-capsule__segments-selector .a-item-selector-dropdown')
    private _segmentsSelector: ItemSelectorDropdownComponent = null;
    @ChildRef('.p-capsule__licenseStart')
    private _licenseStartElement: HTMLDivElement = null;
    @ChildRef('.p-capsule__licenseEnd')
    private _licenseEndElement: HTMLDivElement = null;
    @ChildRef('.o-grid')
    private _grid: GridComponent = null;
    @ChildrenRef(IconComponent, '.a-icon--add')
    private _favoriteIcons: IconComponent[] = null;
    @ChildRef('.a-tab')
    private _tab: TabComponent = null;

    public constructor(node: HTMLElement, 
        private _apiService: ApiService,
        private _sessionService: SessionService,
        private _mediaPlayerService: MediaPlayerService,
        private _userWatchingHistoryService: UserWatchingHistoryService,
        private _userFavoriteService: UserFavoriteService,
        private _dataEventService: DataEventService) {
        super(node);

        this.addWindowEventListener('user-favorites-changed', () => {
            if (this._sessionService.isSigned) {
                this.setFavoriteOption();
            }
        });

        this.addCustomWindowEventListener('pop-capsule-player', e => {
            if (location.pathname.endsWith('/play') || location.pathname.endsWith('/play-trailer')) {
                this.loadPlayer(false, location.pathname.endsWith('/play-trailer') ? 'trailer': 'capsule');
            }
            else if (!this._mediaPlayerService.isMiniPlayerActive(this._playerType)) {
                this._mediaPlayerService.closePlayer(this._playerType);
            }
            this.checkUserWatchingHistory();
            e.isHandled = true;
        });
        this._mediaPlayerService.on('player-closed', async () => {
            this.enable('.p-capsule__start-watch');
            this.enable('.p-capsule__trailer');
            this.enable('.p-capsule__unsigned-trailer');
            this.checkUserWatchingHistory();
            if (location.pathname.endsWith('/play') || location.pathname.endsWith('/play-trailer')) {
                history.back();
            }
        }, this);
        this._mediaPlayerService.on('mini-player-activated', async () => {
            this.disable('.p-capsule__start-watch');
            this.disable('.p-capsule__trailer');
            this.disable('.p-capsule__unsigned-trailer');
            if (this._playerBack) {
                history.back();
            }
            else if (location.pathname.endsWith('/play')) {
                this.replaceState(`${location.pathname.replace('/play', '')}${location.search}`, { popHandlerEvent: null });
            }
            else if (location.pathname.endsWith('/play-trailer')) {
                this.replaceState(`${location.pathname.replace('/play-trailer', '')}${location.search}`, { popHandlerEvent: null });
            }
        }, this);
        this._mediaPlayerService.on('mini-player-deactivated', async () => {
            this.enable('.p-capsule__start-watch');
            this.enable('.p-capsule__trailer');
            this.enable('.p-capsule__unsigned-trailer');
            if (this._mediaPlayerService.currentMedia(this._playerType)) {
                this.setPlayState(true, this._mediaPlayerService.currentMedia(this._playerType).type);
            }
        }, this);

        this.addWindowEventListener('resize', () => this.checkOptionsTooltip());

        if (this.embed) {
            this.setEmbed();
        }
    }

    public onInit(mode: 'load' | 'redirect'): void {
        super.onInit(mode);
        this._playerBack = mode == 'redirect';
        if (this._sessionService.isSigned) {
            this.setFavoriteOption();
            this.removeClass('u-hidden-important', '.p-capsule__segments-container');
        }
        this.addClass(this._sessionService.isSigned || this._freePlay ? 'p-capsule__watch--signed': 'p-capsule__watch--unsigned', '.p-capsule__watch');        
        this.addOrRemoveClass(this._sessionService.isSigned || this._freePlay, 'u-hidden-important', '.p-capsule__register-message');
        this.checkUserWatchingHistory();
        if (location.pathname.endsWith('/play') || location.pathname.endsWith('/play-trailer')) {
            this.play(false);
            this._playerBack = false;
        }
        else if (new URLSearchParams(location.search).get('play') === 'true') {
            this.replaceState(location.pathname);
            this.play(true);
            this._playerBack = false;
        }
        this.setLicenseDates();
        this.checkPlaying();
        this.checkOptionsTooltip();
        
        if (this.embed) {
            this.play(false);
        }
        const tabId: string = new URLSearchParams(location.search).get('tab');
        if (tabId) {
            this._tab.setSelected(Str.slugToCamel(tabId));
        }
    }

    private setEmbed(): void {
        this.addClass('p-capsule--embed');
        document.querySelector<HTMLDivElement>('.t-default__content').style.display = 'block';
    }

    @EventListener('selection-changed', '.a-tab')
    public onTabSelectionChanged(e: CustomEvent): void {
        this.setSelectedTab(e.detail.id);
    }

    private setSelectedTab(id: string): void {
        const tabs: NodeListOf<HTMLDivElement> = this._node.querySelectorAll<HTMLDivElement>('.p-capsule__details-tab, .p-capsule__list-items-tab, .p-capsule__related-tab');
        tabs.forEach((tab: HTMLDivElement) => {
            tab.classList.add('u-hidden');
        });
        this._node.querySelector<HTMLDivElement>(`.p-capsule__${Str.camelToKebab(id)}-tab`).classList.remove('u-hidden');
        this.replaceState(`${location.pathname}?tab=${Str.camelToKebab(id)}`);
    }

    private checkOptionsTooltip(): void {
        const buttons: HTMLButtonElement[] = Array.from(this._node.querySelectorAll<HTMLButtonElement>('.p-capsule__options button'));
        buttons.forEach(button => {
            const textElement: HTMLElement = button.querySelector(':scope > span');
            if (textElement) {
                button.dataset.tooltipEnabled = getComputedStyle(textElement).display === 'none' ? 'true': 'false';
            }
        });
    }

    private setLicenseDates(): void {
        if (this._isComingSoon && this._licenseStart) {
            const hasTime: boolean = DateTime.licenseDateHasTime(this._licenseStart);
            this._licenseStartElement.innerHTML = (hasTime ? USER_LOCALE.availableFromWithTime: USER_LOCALE.availableFrom).format(DateTime.format(this._licenseStart, 'long-date'), DateTime.format(this._licenseStart, 'short-time'));
            this._licenseStartElement.classList.remove('u-hidden');
        }
        else if (this._licenseEnd) {
            const hasTime: boolean = DateTime.licenseDateHasTime(this._licenseEnd);
            this._licenseEndElement.innerHTML = (hasTime ? USER_LOCALE.availableUntilWithTime: USER_LOCALE.availableUntil).format(DateTime.format(this._licenseEnd, 'long-date'), DateTime.format(this._licenseEnd, 'short-time'));
            this._licenseEndElement.classList.remove('u-hidden');
        }
    }

    @ClickListener('.p-capsule__start-watch')
    public startWatch(): void {
        this.loadPlayer();        
    }

    @ClickListener('.p-capsule__trailer')
    public trailer(): void {
        this.loadPlayer(true, 'trailer');
    }

    @ClickListener('.p-capsule__unsigned-trailer')
    public unsignedTrailer(): void {
        this.loadPlayer(true, 'trailer');
    }       

    @ClickListener('.p-capsule__toggle-favorite')
    public onToogleFavorite(): void {
        this.setFavorite();
    }

    @ClickListener('.p-capsule__remove-continue-watching')
    public onRemoveContinueWatching(): void {
        this.removeContinueWatching();
    }

    @ClickListener('.p-capsule__segments')
    public onSelectSegment(e: MouseEvent): void {
        this.toggleSegments(e);
    }

    private async toggleSegments(e: MouseEvent): Promise<void> {
        this._segmentIsOpened = false;
        const selected: string = await this._segmentsSelector.toggle({ selected: null, element: this._selectSegmentElement, mode: 'centered' }, e);        
        const { brightcoveId, contentMediaId, display } = this._node.dataset;
        if (!this._segmentIsOpened) {
            this._dataEventService.openFragment(this._playerType === 'v2' ? contentMediaId : brightcoveId, display, 'landing');
        }
        this._segmentIsOpened = true;
        if (selected) {
            const segment = (this._segments || []).find(s => s.id == parseInt(selected));
            if (segment && segment.position) {
                this.loadPlayer(true, 'capsule', segment);
                this._dataEventService.selectFragment(this._playerType === 'v2' ? contentMediaId : brightcoveId, display, 'landing', segment.title);
            }
        }
    }

    private async setFavorite(): Promise<void> {
        if (this._userFavoriteService.isFavorite(this._capsuleId, 'capsule')) {
            if (await this.showQuestion(USER_LOCALE.removeFromFavorite, USER_LOCALE.removeFromFavoriteQuestion.format('<br><br>'), 'ok', { result: 'cancel' }, { result: 'ok' }) == 'ok') {
                await this._userFavoriteService.remove(this._capsuleId, 'capsule');
                this._dataEventService.favorite(false, new URL(location.href).pathname);
            }
        }
        else {
            await this._userFavoriteService.add(this._capsuleId, 'capsule');
            this._dataEventService.addFavorite('video', this._node.dataset.brightcoveId, this._node.dataset.display, this._node.dataset.disciplines, this._node.dataset.typology);
            this._dataEventService.favorite(true, new URL(location.href).pathname);
        }
    }

    private setFavoriteOption(): void {
        const isFavorite: boolean = this._userFavoriteService.isFavorite(this._capsuleId, 'capsule');
        this.removeClass('u-hidden-important', '.p-capsule__toggle-favorite');
        this._favoriteIcons.forEach(i => i.key = isFavorite ? 'remove': 'add');
    }

    private async removeContinueWatching(): Promise<void> {
        await this._apiService.post(`/api/v1/user/watching/archive/${this._collectionId ? 'collection' : 'capsule'}/${this._collectionId ? this._collectionId: this._capsuleId}`);
        this._userWatchingHistoryService.archive(this._collectionId ? this._collectionId: this._capsuleId, this._collectionId ? 'collection': 'capsule');
        this.addClass('u-hidden-important', '.p-capsule__remove-continue-watching');
        this.checkUserWatchingHistory();
    }

    private checkUserWatchingHistory(): void {
        if (this._isComingSoon) {
            this.setInnerHTML(USER_LOCALE.comingSoonTag, '.p-capsule__start-watch span');
            this.disable('.p-capsule__start-watch');
        }
        else if (this._userWatchingHistoryService.isWatching(this._capsuleId, 'capsule')) {
            this.setInnerHTML(this._type == CONFIG.CAPSULE_VIDEO_TYPE ? USER_LOCALE.continueWatching: USER_LOCALE.continueListening, '.p-capsule__start-watch span');
            if (!this._userWatchingHistoryService.getItem(this._collectionId ? this._collectionId: this._capsuleId, this._collectionId ? 'collection': 'capsule')?.isArchived) {
                this.removeClass('u-hidden-important', '.p-capsule__remove-continue-watching');
            }
        }
        else {
            this.setInnerHTML(this._type == CONFIG.CAPSULE_VIDEO_TYPE ? USER_LOCALE.startWatch: USER_LOCALE.startListen, '.p-capsule__start-watch span');
        }
        if (this._grid) {
            (this._grid.itemElements || []).forEach(item => {
                const id: number = parseInt(item.dataset.id);
                // const watchingHistory: any = this._userWatchingHistoryService.getItem(id, 'capsule');
                // if (watchingHistory) {
                //     this._grid.setProgress(id, watchingHistory);
                // }
                if (id == this._capsuleId) {
                    item.classList.add('o-grid__item--disabled');
                }
            });
        }
    }

    private play(pushState: boolean = true): void {        
        if (!this.checkPlaying()) {            
            this.loadPlayer(pushState, location.pathname.endsWith('/play-trailer') || location.pathname.endsWith('/embed-trailer') ? 'trailer': 'capsule');
        }
    }  
    
    private checkPlaying(): boolean {
        const options: MediaOptions = this._mediaPlayerService.currentMedia(this._playerType) || {} as any;
        if (options.capsuleId == this._capsuleId) {
            this.disable('.p-capsule__start-watch');
            this.disable('.p-capsule__trailer');
            this.disable('.p-capsule__unsigned-trailer');        
            this.setPlayState(!location.pathname.endsWith('/play') && !location.pathname.endsWith('/play-trailer'), this._mediaPlayerService.currentMedia(this._playerType).type);
            return true;
        }
        return false;
    }  

    private async loadPlayer(pushState: boolean = true, type: MediaType = 'capsule', segment?: any): Promise<void> {                
        this._mediaPlayerService.loadMedia(this._playerType, {
            capsuleId: this._capsuleId,
            collectionId: this._collectionId,
            type,
            image: this._image,
            currentTime: segment?.position,
            owner: {
                pathname: location.pathname,
                target: Str.getRelativeUrl(location.href)
            },
            embed: this.embed
        });
        this.setPlayState(pushState, type);
    }

    private setPlayState(pushState: boolean = true, type: MediaType): void {
        if (!this._mediaPlayerService.isMiniPlayerActive(this._playerType)) {
            this.replaceState(`${location.pathname}${location.search}`, { popHandlerEvent: 'pop-capsule-player' });
            if (pushState) {
                this.pushState(type == 'trailer' ? `${location.pathname}/play-trailer`: `${location.pathname}/play`, { popHandlerEvent: 'pop-capsule-player' });
            }
        }
    }   

    private get embed(): boolean {
        return location.pathname.endsWith('/embed') || location.pathname.endsWith('/embed-trailer');
    }

    public dispose(): void {
        this._mediaPlayerService.off(this);
        super.dispose();
    }
}